import { useFrame, useThree } from '@react-three/fiber';
import React, { memo, useMemo, useState } from 'react';
import { OrthographicCamera } from 'three';
import { useGetRaycastFromCamera } from '../../hooks/general';
import { getScreenContentIdFromSceneId } from '../../utils';
import { ITransCatTypes, useDataStore, useSceneStore } from '../../zustand';
import withButtonBehaviour from '../hoc/withButtonBehaviour';
import withImageBehaviour from '../hoc/withImageBehaviour';
import withModel3dBehaviour from '../hoc/withModel3dBehaviour';
import withTextBehaviour from '../hoc/withTextBehaviour';
import withVideoBehaviour from '../hoc/withVideoBehaviour';
import { Button, Image, Model3d, Text, Text3d, Video } from '../r3f-components';
import { IComponentType, IScreenAnchorGroup, IScreenAnchorPositionType, IScreenContent, ITrackingTypes } from '../r3f-components/component-data-structure';
import { getSnapshotButtonId } from '../r3f-components/utils/general';
import { getAnchorGroupPrositionDict } from '../r3f-components/utils/screen-relative';
import withText3dBehaviour from '../hoc/withText3dBehaviour';
import { useGetActiveTrackingType } from '../hoc/hocCustomHooks';
import { TOP_SAFE_SPACE_IN_PIXELS } from '@/constants';
import { Jute } from '@/index';

const ScreenRelativeImageHOC = memo(withImageBehaviour(Image));
ScreenRelativeImageHOC.displayName = 'Screen_Relative_Image_HOC'; // helps with identification in debugger
const ScreenRelativeButtonHOC = memo(withButtonBehaviour(Button));
ScreenRelativeButtonHOC.displayName = 'Screen_Relative_Button_HOC';
const ScreenRelativeVideoHOC = memo(withVideoBehaviour(Video));
ScreenRelativeVideoHOC.displayName = 'Screen_Relative_Video_HOC';
const ScreenRelativeTextHOC = memo(withTextBehaviour(Text));
ScreenRelativeTextHOC.displayName = 'Screen_Relative_Text_HOC';
const ScreenRelativeText3dHOC = memo(withText3dBehaviour(Text3d));
ScreenRelativeText3dHOC.displayName = 'Screen_Relative_Text3d_HOC'; 
const ScreenRelativeModel3dHOC = memo(withModel3dBehaviour(Model3d));
ScreenRelativeModel3dHOC.displayName = 'Screen_Relative_Model3d_HOC';

interface IParentProps {
	cameraRef: React.RefObject<OrthographicCamera>;
	sceneId: string;
}

const AnchorGroups: React.FunctionComponent<IParentProps> = ({ sceneId, cameraRef }) => {
	const { size, scene } = useThree();
	const activeSceneId = useSceneStore(({ activeSceneId }) => activeSceneId);
	const componentsById = useDataStore((state) => state.componentsById);
	const screenContentId = getScreenContentIdFromSceneId(sceneId, componentsById);
	const [screenAspectRatio, setScreenAspectRatio] = useState(size.width / size.height);
	const snapshotButtonId = getSnapshotButtonId(componentsById, activeSceneId);
	const transitionCategory = useSceneStore((state) => state.transitionCategory[sceneId]);
	const raycast = useGetRaycastFromCamera(cameraRef);
	const activeTrackingType = useGetActiveTrackingType();

	const isFaceTrackingActive = activeTrackingType === ITrackingTypes.face;

	const getAnchorGroupPosition = (anchorPositionType: IScreenAnchorPositionType, isFaceTrackingActive: boolean) => {
		const position = anchorGroupPositionDict[anchorPositionType];

		const safeSpaceAdjustmentRequired = (
			anchorPositionType !== IScreenAnchorPositionType.leftBottom && 
			anchorPositionType !== IScreenAnchorPositionType.rightBottom && 
			anchorPositionType !== IScreenAnchorPositionType.bottomMiddle && 
			((!isFaceTrackingActive && Jute.isMobileWebXRExperience) || Jute.isAppClip)
		) ;

		if (safeSpaceAdjustmentRequired) {	
			const orthCam = scene.getObjectByName('screenRelativeCamera') as OrthographicCamera;
			const pixelsPerCoord = (size.height / (orthCam.top - orthCam.bottom)) * orthCam.zoom;
			const topSafeSpaceCoords = TOP_SAFE_SPACE_IN_PIXELS / pixelsPerCoord;
			position[1] = position[1] - topSafeSpaceCoords;
		}

		return position;
	}
	

	useFrame(({ size }) => {
		const newRatio = size.width / size.height;
		if (newRatio !== screenAspectRatio) setScreenAspectRatio(newRatio);
	});

	const anchorGroupPositionDict = getAnchorGroupPrositionDict(size);
	const screenContent = componentsById?.[screenContentId || ''] as IScreenContent | undefined;

	const anchorGroups = useMemo(
		() =>
			screenContent?.children.map((id) => {
			if (transitionCategory === ITransCatTypes.hidden) return null;
				const anchorGroup = componentsById?.[id] as IScreenAnchorGroup;

				const anchorR3fComponents = anchorGroup.children.map((id) => {
					if (id === snapshotButtonId || !componentsById) return null;
					const component = componentsById[id];

					switch (component.type) {
						case IComponentType.Button:
							return <ScreenRelativeButtonHOC key={id} id={id} sceneId={sceneId} screenRelative={true} raycast={raycast} />;
						case IComponentType.Image:
							return <ScreenRelativeImageHOC key={id} id={id} sceneId={sceneId} screenRelative={true} raycast={raycast} />;
						case IComponentType.Text:
							return <ScreenRelativeTextHOC key={id} id={id} sceneId={sceneId} screenRelative={true} raycast={raycast} />;
						case IComponentType.Text3d:
							return <ScreenRelativeText3dHOC key={id} id={id} sceneId={sceneId} screenRelative={true} raycast={raycast} />;
						case IComponentType.Video:
							return <ScreenRelativeVideoHOC key={id} id={id} sceneId={sceneId} screenRelative={true} raycast={raycast} />;
						case IComponentType.Model3d:
							return <ScreenRelativeModel3dHOC key={id} id={id} sceneId={sceneId} screenRelative={true} raycast={raycast} />;
						default:
							return null;
					}
				});

				return (
					<group
						key={anchorGroup.anchorPositionType}
						name={anchorGroup.anchorPositionType}
						scale={1}
						position={getAnchorGroupPosition(anchorGroup.anchorPositionType, isFaceTrackingActive)}
						visible={activeSceneId === sceneId}
					>
						{anchorR3fComponents}
					</group>
				);
			}),
		[screenAspectRatio, activeSceneId, snapshotButtonId, transitionCategory, isFaceTrackingActive]
	); // eslint-disable-line react-hooks/exhaustive-deps

	return <>{anchorGroups || null}</>;
};

export default AnchorGroups;
